import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Input } from '@ui/components/ui/input';
import { Label } from '@ui/components/ui/label';
import { Button } from '@ui/components/ui/button';
import { useChangePassword } from '@data';

interface PasswordForm {
  current: string;
  next: string;
  confirm: string;
}

// Takes the Account section's place while open: a task with a beginning and an end, closed by save or cancel.
const ChangePasswordSection = ({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }): JSX.Element => {
  const { t } = useTranslation();
  const [failed, setFailed] = useState<string | null>(null);
  const { mutate: changePassword, isPending } = useChangePassword();
  const {
    control,
    handleSubmit,
    watch,
    setError,
    formState: { errors },
  } = useForm<PasswordForm>({ defaultValues: { current: '', next: '', confirm: '' } });

  const onSubmit = ({ current, next }: PasswordForm): void => {
    setFailed(null);
    changePassword(
      { currentPassword: current, newPassword: next },
      {
        onSuccess: () => onDone(),
        onError: err => {
          const reason = (err as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
          // A wrong current password belongs on the field that is wrong; anything else is not about
          // one field, so it goes above the buttons.
          if (reason === 'invalidCurrentPassword') {
            setError('current', { message: t('settings.password.errors.wrongCurrent') });
          } else {
            setFailed(t('settings.password.errors.failed'));
          }
        },
      },
    );
  };

  const required = t('settings.password.errors.required');

  return (
    <section>
      <p className="text-xs text-muted-foreground">{t('settings.password.description')}</p>

      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
        <Controller
          name="current"
          control={control}
          rules={{ required }}
          render={({ field }) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="settings-current-password">{t('settings.password.current')}</Label>
              <Input id="settings-current-password" type="password" autoComplete="current-password" {...field} />
              {errors.current && <p className="text-sm text-destructive">{errors.current.message}</p>}
            </div>
          )}
        />

        <Controller
          name="next"
          control={control}
          rules={{ required, minLength: { value: 8, message: t('settings.password.errors.min') } }}
          render={({ field }) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="settings-new-password">{t('settings.password.new')}</Label>
              <Input id="settings-new-password" type="password" autoComplete="new-password" {...field} />
              {errors.next && <p className="text-sm text-destructive">{errors.next.message}</p>}
            </div>
          )}
        />

        <Controller
          name="confirm"
          control={control}
          rules={{
            required,
            validate: value => value === watch('next') || t('settings.password.errors.mismatch'),
          }}
          render={({ field }) => (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="settings-confirm-password">{t('settings.password.confirm')}</Label>
              <Input id="settings-confirm-password" type="password" autoComplete="new-password" {...field} />
              {errors.confirm && <p className="text-sm text-destructive">{errors.confirm.message}</p>}
            </div>
          )}
        />

        {failed && <p className="text-sm text-destructive">{failed}</p>}

        <div className="flex items-center gap-3">
          <Button type="submit" loading={isPending}>
            {t('settings.password.save')}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isPending}>
            {t('actions.cancel')}
          </Button>
        </div>
      </form>
    </section>
  );
};

export default ChangePasswordSection;
